import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiService, ApiError } from '../services/api'
import type { UserActivity, UserSegmentation } from '../types'
import { formatNumber } from '../utils/formatters'
import TabNavigation from '../components/TabNavigation'
import { useDateRange } from '../context/DateRangeContext'
import { useNotification } from '../context/NotificationContext'
import { useLanguage } from '../context/LanguageContext'
import FilterPanel from '../components/FilterPanel'

export default function UsersDetail() {
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [users, setUsers] = useState<UserActivity[]>([])
  const [segmentation, setSegmentation] = useState<UserSegmentation | null>(null)
  const [filter, setFilter] = useState<'all' | 'power' | 'regular' | 'low' | 'non_adopter'>('all')
  const [selectedApps, setSelectedApps] = useState<string[]>([])
  const [selectedActions, setSelectedActions] = useState<string[]>([])
  const [availableApps, setAvailableApps] = useState<string[]>([])
  const [availableActions, setAvailableActions] = useState<string[]>([])
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const { startDate, endDate, daysDiff } = useDateRange()
  const { showNotification } = useNotification()
  const { t, language } = useLanguage()

  useEffect(() => {
    if (startDate && endDate) {
      loadInitialData()
      loadAvailableFilters()
    }
  }, [startDate, endDate])

  useEffect(() => {
    if (startDate && endDate && selectedApps.length > 0) {
      loadUsers()
      loadAvailableFilters() // Update available filters based on selection
    }
  }, [selectedApps, selectedActions])

  const loadInitialData = async () => {
    // Don't load data here - wait for filters to be set first
    // loadAvailableFilters will set filters, which triggers loadUsers
    setLoading(true)
  }

  const loadUsers = async () => {
    setTableLoading(true)
    try {
      // If all filters are selected, don't pass any filters (same as dashboard)
      const allAppsSelected = selectedApps.length === availableApps.length
      const allActionsSelected = selectedActions.length === availableActions.length
      const appsFilter = allAppsSelected ? undefined : selectedApps
      const actionsFilter = allActionsSelected ? undefined : selectedActions

      // Load both users and segmentation with the same filters
      const [usersData, segData] = await Promise.all([
        apiService.getUserActivityByRange(
          startDate,
          endDate,
          1000,
          appsFilter,
          actionsFilter
        ),
        apiService.getUserSegmentationByRange(
          startDate,
          endDate,
          appsFilter,
          actionsFilter
        )
      ])
      setUsers(usersData)
      setSegmentation(segData)
    } catch (error) {
      console.error('Error loading user data:', error)

      if (error instanceof ApiError) {
        const [title, ...messageParts] = error.userMessage.split(': ')
        showNotification('error', title, messageParts.join(': '))
      } else {
        showNotification(
          'error',
          t.errors.failedToFilter,
          t.errors.tryAgain
        )
      }
    } finally {
      setTableLoading(false)
      setLoading(false) // Also clear initial loading state
    }
  }

  const loadAvailableFilters = async () => {
    try {
      // Apps: always show all (no filtering)
      // Actions: filter based on selected apps only
      const filters = await apiService.getAvailableFilters(
        startDate,
        endDate,
        selectedApps.length > 0 ? selectedApps : undefined,
        undefined // Never filter apps based on actions
      )
      setAvailableApps(filters.apps)
      setAvailableActions(filters.actions)

      // First load: select all apps and actions
      if (selectedApps.length === 0 && selectedActions.length === 0) {
        setSelectedApps(filters.apps)
        setSelectedActions(filters.actions)
      }
    } catch (error) {
      console.error('Error loading available filters:', error)

      if (error instanceof ApiError) {
        const [title, ...messageParts] = error.userMessage.split(': ')
        showNotification('warning', title, messageParts.join(': '))
      } else {
        showNotification(
          'warning',
          t.errors.failedToLoadFilters,
          t.errors.someOptionsUnavailable
        )
      }
    }
  }

  const handleApplyFilters = (apps: string[], actions: string[]) => {
    setSelectedApps(apps)
    setSelectedActions(actions)
  }

  // Calculate dynamic thresholds based on period length
  // Must match backend SEGMENT_THRESHOLDS: power_per_week=35, regular_per_week=8
  const getThresholds = () => {
    const powerThreshold = Math.max(7, Math.round(35 * daysDiff / 7))
    const regularThreshold = Math.max(2, Math.round(8 * daysDiff / 7))
    return { power: powerThreshold, regular: regularThreshold }
  }

  const getUserSegment = (totalEvents: number): string => {
    const thresholds = getThresholds()
    if (totalEvents >= thresholds.power) return 'power'
    if (totalEvents >= thresholds.regular) return 'regular'
    if (totalEvents >= 1) return 'low'
    return 'non_adopter'
  }

  const getSegmentBadge = (segment: string) => {
    const badges = {
      power: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      regular: 'bg-blue-100 text-blue-800 border-blue-300',
      low: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      non_adopter: 'bg-gray-100 text-gray-800 border-gray-300',
    }
    const labels = {
      power: t.segments.powerUser,
      regular: t.segments.regular,
      low: t.segments.lowUsage,
      non_adopter: t.segments.inactive,
    }
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${badges[segment as keyof typeof badges]}`}>
        {labels[segment as keyof typeof labels]}
      </span>
    )
  }

  const filteredUsers = users.filter(user => {
    if (filter === 'all') return true
    return getUserSegment(user.total_activities) === filter
  })

  if (loading || !segmentation) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <>
      <TabNavigation />
      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        selectedApps={selectedApps}
        selectedActions={selectedActions}
        onApply={handleApplyFilters}
        availableApps={availableApps}
        availableActions={availableActions}
      />
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="glass-card rounded-2xl p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-800">{t.usersPage.title}</h1>
          <p className="text-gray-600 mt-1">{t.usersPage.subtitle}</p>
        </div>

        {/* Segmentation Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{t.usersPage.powerUsers}</p>
                <p className="text-3xl font-bold text-yellow-600">{segmentation.power_user}</p>
                <p className="text-xs text-gray-500 mt-1">{getThresholds().power}+ {t.usersPage.activities}</p>
              </div>
              <button
                onClick={() => setFilter(filter === 'power' ? 'all' : 'power')}
                className={`px-3 py-1 rounded-lg text-sm font-medium ${filter === 'power' ? 'bg-yellow-500 text-gray-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {filter === 'power' ? '✕' : t.usersPage.filter}
              </button>
            </div>
          </div>

          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{t.usersPage.regular}</p>
                <p className="text-3xl font-bold text-blue-600">{segmentation.regular}</p>
                <p className="text-xs text-gray-500 mt-1">{getThresholds().regular}-{getThresholds().power - 1} {t.usersPage.activities}</p>
              </div>
              <button
                onClick={() => setFilter(filter === 'regular' ? 'all' : 'regular')}
                className={`px-3 py-1 rounded-lg text-sm font-medium ${filter === 'regular' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {filter === 'regular' ? '✕' : t.usersPage.filter}
              </button>
            </div>
          </div>

          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{t.usersPage.lowUsage}</p>
                <p className="text-3xl font-bold text-yellow-600">{segmentation.low_activity}</p>
                <p className="text-xs text-gray-500 mt-1">1-{getThresholds().regular - 1} {t.usersPage.activities}</p>
              </div>
              <button
                onClick={() => setFilter(filter === 'low' ? 'all' : 'low')}
                className={`px-3 py-1 rounded-lg text-sm font-medium ${filter === 'low' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {filter === 'low' ? '✕' : t.usersPage.filter}
              </button>
            </div>
          </div>

          <div className="glass-card rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{t.usersPage.inactive}</p>
                <p className="text-3xl font-bold text-gray-600">{segmentation.non_adopter}</p>
                <p className="text-xs text-gray-500 mt-1">0 {t.usersPage.activities}</p>
              </div>
              <button
                onClick={() => setFilter(filter === 'non_adopter' ? 'all' : 'non_adopter')}
                className={`px-3 py-1 rounded-lg text-sm font-medium ${filter === 'non_adopter' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {filter === 'non_adopter' ? '✕' : t.usersPage.filter}
              </button>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="glass-card rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800">
                {filter === 'all' ? t.usersPage.allUsers :
                 filter === 'non_adopter' ? t.usersPage.inactiveUsers :
                 filter === 'power' ? t.usersPage.powerUsers :
                 filter === 'regular' ? t.usersPage.regularUsers :
                 t.usersPage.lowActivityUsers}
                <span className="text-gray-500 font-normal ml-2">({filteredUsers.length})</span>
              </h2>
              {filter !== 'all' && (
                <button
                  onClick={() => setFilter('all')}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                >
                  {t.usersPage.showAll}
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Filter Button */}
              <button
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                {t.usersPage.filters}
              </button>

              {tableLoading && (
                <div className="loading-spinner w-5 h-5"></div>
              )}
            </div>
          </div>

          <div className="overflow-x-auto relative">
            {tableLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                <div className="loading-spinner"></div>
              </div>
            )}
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">#</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">{t.usersPage.user}</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">{t.usersPage.segment}</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">{t.usersPage.activities}</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">{t.usersPage.uniqueActions}</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">{t.usersPage.activeDays}</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">{t.usersPage.lastActivity}</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, idx) => {
                  const segment = getUserSegment(user.total_activities)
                  return (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 text-sm text-gray-600">{idx + 1}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-gray-900 font-bold text-sm">
                            {user.user_email.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-medium text-gray-800">{user.user_email}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {getSegmentBadge(segment)}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-semibold text-gray-800">
                        {formatNumber(user.total_activities)}
                      </td>
                      <td className="py-3 px-4 text-right text-sm text-gray-600">
                        {user.unique_actions}
                      </td>
                      <td className="py-3 px-4 text-right text-sm text-gray-600">
                        {user.active_days}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {user.last_seen ? new Date(user.last_seen).toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US') : '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>
    </>
  )
}
